import { Link } from "react-router-dom";
import {
    ShieldCheck, ShieldAlert, Wifi, Wrench, Download, Bug,
    Phone, ArrowRight, CheckCircle2, AlertTriangle, Network,
    Printer, ServerCrash, Usb, Database
} from "lucide-react";
import StarRating from "@/components/StarRating";
import { CONTACT } from "@/lib/constants";

const THREATS = [
    {
        icon: Wifi,
        title: "The network foothold",
        copy: "Printers are the least-patched devices on most networks. An attacker who compromises your printer gains a quiet, always-on presence inside your LAN. From there they can pivot to your PCs, NAS, and cloud accounts.",
        severity: "High",
    },
    {
        icon: Printer,
        title: "PrintNightmare and spooler CVEs",
        copy: "The Windows Print Spooler has a long history of remote code execution bugs — PrintNightmare (2021) being the most famous. Printers expose ports 9100, 515, and 631 to local networks, and the spooler turns 'anyone can print' into 'anyone can execute code.'",
        severity: "Critical",
    },
    {
        icon: Bug,
        title: "Firmware-level exploitation",
        copy: "Printer firmware is rarely updated and runs with full network privileges. Advanced persistent threat (APT) groups have used compromised printer firmware to persist inside networks after other entry points were closed.",
        severity: "High",
    },
    {
        icon: Download,
        title: "Rogue and typosquatted drivers",
        copy: "Searches like 'HP driver download' routinely lead to lookalike sites serving malware. Official drivers come only from the manufacturer's site or Windows Update — never from third-party 'driver fixer' tools.",
        severity: "High",
    },
    {
        icon: Network,
        title: "DNS and print-job manipulation",
        copy: "A compromised printer can redirect DNS queries, capture print jobs containing sensitive data, and use outbound connections to exfiltrate documents. In enterprise environments, this is a classic data-loss vector.",
        severity: "Medium",
    },
    {
        icon: ServerCrash,
        title: "Lateral movement and cryptomining",
        copy: "Printers with sufficient CPU (color lasers, business MFPs) can host cryptominers, siphoning CPU cycles without any user-visible symptom. This is exactly how Mirai variants and IoT botnets have historically proliferated.",
        severity: "Medium",
    },
];

const ANTIVIRUS = [
    {
        id: "bitdefender-total-security",
        name: "Bitdefender Total Security",
        rating: 4.8,
        price_usd: 49.99,
        strength: "Best network-level protection",
        note: "Exceptional at catching malicious downloads before they reach your printer's driver layer. Web filtering, exploit protection, and a hardened sandbox are all first-class.",
        pros: ["Excellent exploit protection", "Low performance impact", "Strong web threat filtering", "Cross-platform"],
        cons: ["Interface is dense", "Advanced features require deep menu diving"],
        best_for: "Users who want set-and-forget enterprise-grade network defense",
    },
    {
        id: "microsoft-defender",
        name: "Microsoft Defender (built into Windows)",
        rating: 4.5,
        price_usd: 0,
        strength: "Free, capable, and already installed",
        note: "Defender has quietly become competitive with paid AV. For most home users, it's the sensible default — especially when paired with sensible browsing habits and firewall hygiene.",
        pros: ["Free with Windows", "Deep OS integration", "Low false positives", "No nagware"],
        cons: ["No cross-platform", "Some features require Microsoft account", "Limited parental controls"],
        best_for: "Windows-only households on a budget",
    },
    {
        id: "malwarebytes-premium",
        name: "Malwarebytes Premium",
        rating: 4.4,
        price_usd: 39.99,
        strength: "Excellent on-demand scanner and cleanup tool",
        note: "The go-to tool for 'I think something bad already happened' scenarios. Particularly strong at cleaning up after typosquatted driver downloads and fake printer support tools.",
        pros: ["Superb cleanup engine", "Light footprint", "Cross-platform", "Browser guard is excellent"],
        cons: ["Real-time protection lags Bitdefender", "No VPN at this tier", "Limited firewall"],
        best_for: "Second-line-of-defense on a clean system",
    },
    {
        id: "norton-360-deluxe",
        name: "Norton 360 Deluxe",
        rating: 4.2,
        price_usd: 59.99,
        strength: "Most bundled features per dollar",
        note: "Includes VPN, dark web monitoring, password manager, and parental controls. The AV engine itself is solid, and the bundled identity-protection features add real value for households.",
        pros: ["Feature-rich bundle", "VPN included", "Identity theft protection", "5 devices per license"],
        cons: ["Heavy system impact", "Interface pushes upsells", "Auto-renew pricing is aggressive"],
        best_for: "Households wanting an all-in-one security suite",
    },
    {
        id: "kaspersky-standard",
        name: "Kaspersky Standard",
        rating: 4.3,
        price_usd: 44.99,
        strength: "Strongest raw detection rates in independent lab tests",
        note: "Kaspersky's engine consistently leads third-party AV-Test and AV-Comparatives scores. Note: US government users should avoid Kaspersky due to regulatory concerns. For private users elsewhere, detection rates are hard to beat.",
        pros: ["Top-tier detection rates", "Efficient engine", "Good parental controls", "Reasonable price"],
        cons: ["US regulatory context", "Geopolitically controversial", "Some features locked to higher tiers"],
        best_for: "Private users prioritizing raw detection rate (non-US)",
    },
    {
        id: "eset-nod32",
        name: "ESET NOD32",
        rating: 4.4,
        price_usd: 39.99,
        strength: "Lightweight with excellent scanner",
        note: "Favored by IT professionals for a reason: extremely low performance overhead, near-zero false positives, and a scanner that catches things other tools miss. Minimal interface, maximum signal.",
        pros: ["Very low performance impact", "Excellent scanner", "Minimal false positives", "Favored by IT pros"],
        cons: ["Plain interface", "Fewer bundled extras", "No VPN"],
        best_for: "Power users who want fast, unobtrusive protection",
    },
];

const PRINTER_HARDENING = [
    "Disable Wi-Fi Protected Setup (WPS) — it's a known attack vector",
    "Disable unused services: FTP, Telnet, and web admin if you don't use them",
    "Update firmware directly from the manufacturer's site, never via third-party tools",
    "Set a strong admin password (factory default is often 'admin' or blank)",
    "Put the printer on a guest network or separate VLAN if your router supports it",
    "Disable mDNS/Bonjour if you don't use AirPrint",
    "Block inbound port 9100 from the internet — printers should never be publicly routable",
    "Enable encrypted print queues (IPPS) if your printer supports it",
];

export default function Security() {
    return (
        <div data-testid="security-page">
            {/* JSON-LD for SEO */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": "How Viruses and Malware Actually Affect Your Printer (and the Best Antivirus for 2026)",
                        "description": "Printers don't get viruses the way PCs do — but they're exploited in other ways. A clear breakdown plus a hands-on comparison of the top antivirus tools.",
                        "author": { "@type": "Organization", "name": "All About Printers" },
                        "publisher": { "@type": "Organization", "name": "All About Printers" },
                        "mainEntityOfPage": "https://allaboutprinters.blog/security",
                    })
                }}
            />

            {/* Hero */}
            <section className="container-tight pt-16 md:pt-24 pb-12 border-b border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="eyebrow">Security · Printer Threats</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3 leading-[1.02]">
                            Your printer can be the <span className="text-yellow-400">weakest link</span>{" "}
                            on your network.
                        </h1>
                        <p className="text-lg text-zinc-400 mt-5 max-w-2xl leading-relaxed">
                            Printers don't get "viruses" the way PCs do — but they're routinely used as entry points
                            into otherwise-secure networks. Here's how malware actually affects printers, plus an
                            honest comparison of the antivirus tools worth using in 2026.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 border border-yellow-500/30 bg-yellow-500/10 px-3.5 py-2 rounded-sm text-xs text-yellow-100">
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>For informational purposes — always verify current CVEs and vendor advisories.</span>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="card-flat p-6">
                            <ShieldCheck className="h-8 w-8 text-blue-400" />
                            <div className="eyebrow mt-4">Quick answer</div>
                            <p className="text-zinc-300 text-sm mt-2 leading-relaxed">
                                The single best thing you can do for printer security is <strong className="text-white">update firmware</strong> and
                                <strong className="text-white"> put it on a separate VLAN</strong> or guest network.
                                Pair that with a real antivirus on every PC — because almost every printer attack
                                rides in through an infected PC, not the printer itself.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Threat scenarios */}
            <section className="container-tight py-16 md:py-20 border-b border-zinc-800">
                <div className="mb-10">
                    <div className="eyebrow">01 · Threat landscape</div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
                        Six real ways malware touches your printer
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800" data-testid="threat-grid">
                    {THREATS.map((t, i) => (
                        <div key={t.title} className="bg-zinc-950 p-7 hover:bg-zinc-900 transition-colors duration-200" data-testid={`threat-card-${i}`}>
                            <div className="flex items-start justify-between">
                                <div className="h-10 w-10 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                                    <t.icon className="h-5 w-5 text-blue-400" />
                                </div>
                                <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-medium border rounded-sm ${
                                    t.severity === "Critical" ? "border-red-500/50 text-red-300 bg-red-500/10" :
                                    t.severity === "High" ? "border-yellow-500/50 text-yellow-300 bg-yellow-500/10" :
                                    "border-zinc-700 text-zinc-400"
                                }`}>{t.severity}</span>
                            </div>
                            <h3 className="font-display text-lg font-semibold text-white mt-5 tracking-tight">{t.title}</h3>
                            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{t.copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Antivirus comparison */}
            <section className="container-tight py-16 md:py-20 border-b border-zinc-800">
                <div className="mb-10">
                    <div className="eyebrow">02 · Antivirus comparison</div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
                        The antivirus tools worth using in 2026
                    </h2>
                    <p className="text-zinc-400 mt-3 max-w-2xl leading-relaxed">
                        Since most printer malware arrives via an infected PC, the right endpoint protection is the
                        single highest-leverage defense. These six consistently rank at the top of independent
                        AV-Test and AV-Comparatives labs.
                    </p>
                </div>

                {/* Comparison table */}
                <div className="border border-zinc-800 rounded-sm overflow-x-auto mb-10" data-testid="av-compare-table">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead className="bg-zinc-950 sticky top-0 z-10">
                            <tr>
                                <th className="text-left px-5 py-4 border-b border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500">Antivirus</th>
                                <th className="text-left px-5 py-4 border-b border-l border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500">Rating</th>
                                <th className="text-left px-5 py-4 border-b border-l border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500">From</th>
                                <th className="text-left px-5 py-4 border-b border-l border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500">Best For</th>
                                <th className="text-left px-5 py-4 border-b border-l border-zinc-800 text-xs tracking-[0.2em] uppercase text-zinc-500">Standout</th>
                            </tr>
                        </thead>
                        <tbody className="text-zinc-200">
                            {ANTIVIRUS.map((av, i) => (
                                <tr key={av.id} className={i % 2 === 0 ? "bg-zinc-950/40" : ""}>
                                    <td className="px-5 py-4 border-b border-zinc-900">
                                        <div className="font-display font-semibold text-white">{av.name}</div>
                                    </td>
                                    <td className="px-5 py-4 border-b border-l border-zinc-900"><StarRating value={av.rating} size="sm" /></td>
                                    <td className="px-5 py-4 border-b border-l border-zinc-900 font-mono text-white">${av.price_usd === 0 ? "Free" : av.price_usd.toFixed(2)}</td>
                                    <td className="px-5 py-4 border-b border-l border-zinc-900 text-zinc-300 text-xs leading-relaxed max-w-xs">{av.best_for}</td>
                                    <td className="px-5 py-4 border-b border-l border-zinc-900 text-blue-400 text-xs leading-relaxed max-w-xs">{av.strength}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Detail cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="av-detail-cards">
                    {ANTIVIRUS.map((av) => (
                        <div key={av.id} className="card-flat p-6 md:p-7" data-testid={`av-card-${av.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-display text-xl font-semibold text-white tracking-tight">{av.name}</h3>
                                    <div className="mt-2 flex items-center gap-3">
                                        <StarRating value={av.rating} size="sm" />
                                        <span className="font-mono text-sm text-white">${av.price_usd === 0 ? "Free" : av.price_usd.toFixed(2)}/yr</span>
                                    </div>
                                </div>
                                <span className="text-[10px] tracking-[0.2em] uppercase text-blue-400 border border-blue-500/30 bg-blue-500/10 px-2 py-1 flex-shrink-0">
                                    {av.strength}
                                </span>
                            </div>
                            <p className="text-sm text-zinc-300 mt-4 leading-relaxed">{av.note}</p>
                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
                                <div className="bg-zinc-950 p-4">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 flex items-center gap-1.5">
                                        <CheckCircle2 className="h-3 w-3" /> Pros
                                    </div>
                                    <ul className="mt-2 space-y-1">
                                        {av.pros.map((p, i) => (
                                            <li key={i} className="text-xs text-zinc-300 flex gap-1.5">
                                                <span className="text-emerald-500">+</span>{p}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="bg-zinc-950 p-4">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-red-400 flex items-center gap-1.5">
                                        <ShieldAlert className="h-3 w-3" /> Cons
                                    </div>
                                    <ul className="mt-2 space-y-1">
                                        {av.cons.map((c, i) => (
                                            <li key={i} className="text-xs text-zinc-300 flex gap-1.5">
                                                <span className="text-red-500">–</span>{c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-zinc-900 text-xs text-zinc-500">
                                <span className="text-zinc-400">Best for:</span> {av.best_for}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Hardening checklist */}
            <section className="container-tight py-16 md:py-20 border-b border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="eyebrow">03 · Hardening checklist</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
                            Printer-side hardening in 10 minutes
                        </h2>
                        <p className="text-zinc-400 mt-4 leading-relaxed">
                            The single most common printer compromise vector isn't a fancy zero-day —
                            it's the factory-default admin password and an open 9100 port on the public internet.
                            Spend ten minutes with this list and you'll eliminate 80% of printer-side risk.
                        </p>
                        <div className="mt-8 inline-flex items-center gap-3 text-sm">
                            <div className="h-10 w-10 rounded-sm bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
                                <Phone className="h-4 w-4 text-blue-400" />
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Need help?</div>
                                <a href={CONTACT.phoneHref} data-testid="security-phone-cta" className="font-display font-semibold text-white hover:text-blue-400 transition-colors">
                                    Call {CONTACT.phoneDisplay}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <ol className="space-y-3" data-testid="hardening-list">
                            {PRINTER_HARDENING.map((item, i) => (
                                <li key={i} className="card-flat p-4 flex items-start gap-4">
                                    <span className="font-mono text-xs text-blue-400 border border-blue-500/30 bg-blue-500/10 px-2 py-1 flex-shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-sm text-zinc-200 leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="container-tight py-16 md:py-20">
                <div className="card-flat p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center" data-testid="security-bottom-cta">
                    <div className="md:col-span-8">
                        <div className="eyebrow">Still unsure?</div>
                        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-3">
                            Talk to a printer security expert.
                        </h3>
                        <p className="text-zinc-400 mt-3 leading-relaxed max-w-lg">
                            We'll look at your setup — printer, network, and threat model — and recommend the
                            simplest path to reasonable security. No sales pitch, just a quick, honest assessment.
                        </p>
                    </div>
                    <div className="md:col-span-4 flex flex-col gap-3">
                        <a href={CONTACT.phoneHref} className="btn-primary justify-center" data-testid="security-cta-call">
                            <Phone className="h-4 w-4" /> Call {CONTACT.phoneDisplay}
                        </a>
                        <Link to="/contact" className="btn-ghost justify-center" data-testid="security-cta-contact">
                            Send a message <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
